import { cssVar, fxConfig } from '../config';
import { getLayer } from '../layer';
import { cutCelebrations, hold, onCut, after, liveParticles, particleCap } from './engine';
import type { Stage } from './kit';
import { carp, doudizhu, firecrackers, hongbao, mahjong, zhajinhua } from './presets/eastern';
import { jackpot, luckyLever, neonArcade, pirate } from './presets/machines';
import { colorSort, tileMatch } from './presets/puzzles';
import { crash, fallback, pot, roulette, ROULETTE_COLORS } from './presets/rounds';
import { deep, garden, koiPond, spaceport, sweetShop, workshop } from './presets/slots';
import { chess } from './presets/strategy';
import type { Preset } from './presets/types';
import { words } from './presets/words';
import { shades } from './presets/types';

/**
 * Themed celebrations — one API for every game:
 *
 *   celebrate('garden', celebrationTier(win, bet), winCellsCenter);          // tier by the win / stake
 *   celebrate('roulette', 'big', stripEl, { palette: ROULETTE_COLORS.green });
 *   celebrate('crash', 'mega', cashBtn, { amount: f.int(payout) });           // mega: the big amount if the screen has none
 *   celebrate('jackpot', 'jackpot');                                          // the house jackpot 乐游, above mega
 *
 * Honest play: below the stake (or no win) nothing happens — no particles, no sound; one tap / Esc / Space / Enter
 * stops everything at once (also via useCelebration().skip); reduced motion → no particles, only a soft glow (and
 * the amount on mega). Sound: the presets only add soft pops of their fireworks (rate-limited, silent while sound
 * is off); the win sound itself stays with the game.
 */
export type CelebrationTier = 'win' | 'big' | 'mega' | 'jackpot';

export const PRESETS: Readonly<Record<string, Preset>> = {
  garden,
  deep,
  spaceport,
  'sweet-shop': sweetShop,
  workshop,
  'koi-pond': koiPond,
  carp,
  firecrackers,
  hongbao,
  mahjong,
  'mahjong-table': mahjong,
  zhajinhua,
  doudizhu,
  'tile-match': tileMatch,
  'color-sort': colorSort,
  'daily-words': words,
  chess,
  crash,
  pot,
  roulette,
  'lucky-lever': luckyLever,
  'neon-arcade': neonArcade,
  'pirate-bay': pirate,
  'pirate-cove': pirate,
  jackpot,
  default: fallback,
};

/** Nominal length of a tier (the promise of celebrate() resolves after it, or at once on a skip). */
export const CELEBRATION_MS: Record<CelebrationTier, number> = {
  win: 800,
  big: 2000,
  mega: 3600,
  jackpot: 5000,
};

const ORDER: CelebrationTier[] = ['win', 'big', 'mega', 'jackpot'];

/** Honest-play rule 1: below the stake — null (quiet). Win ≥1×, big ≥10×, mega ≥50× (as slotMath.winTier). */
export function celebrationTier(win: number, stake: number): 'win' | 'big' | 'mega' | null {
  if (!(win > 0) || !(stake > 0) || win < stake) return null;
  if (win >= stake * 50) return 'mega';
  if (win >= stake * 10) return 'big';
  return 'win';
}

/** Raise a tier for a special moment (the Dragon Gate, every ingot found): never makes a quiet win loud. */
export function atLeast<T extends CelebrationTier | null>(
  tier: T,
  min: CelebrationTier,
): T | CelebrationTier {
  if (tier === null) return tier;
  return ORDER.indexOf(tier) >= ORDER.indexOf(min) ? tier : min;
}

export interface CelebrateOptions {
  /** the big amount (already formatted) for mega / jackpot when the screen does not show one itself */
  amount?: string;
  /** colours instead of the game's own (#RRGGBB): the Pot winner, the Roulette sector */
  palette?: readonly string[];
  /** a celebration that should end with the caller's (e.g. useCelebration's signal) */
  signal?: AbortSignal;
}

type Origin = Element | { x: number; y: number } | null | undefined;

function originPoint(o: Origin): { x: number; y: number } {
  if (o && 'getBoundingClientRect' in o) {
    const r = o.getBoundingClientRect();
    if (r.width || r.height) return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  } else if (o && Number.isFinite(o.x) && Number.isFinite(o.y)) return { x: o.x, y: o.y };
  return { x: innerWidth / 2, y: innerHeight * 0.45 };
}

/** #RGB / #RRGGBB / rgb(r, g, b) → #RRGGBB (the glow sprites append an alpha byte); anything else — null. */
function hex(c: string): string | null {
  const s = c.trim();
  if (/^#[0-9a-f]{6}$/i.test(s)) return s.toUpperCase();
  const m3 = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(s);
  if (m3) return ('#' + m3[1] + m3[1] + m3[2] + m3[2] + m3[3] + m3[3]).toUpperCase();
  const rgb = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i.exec(s);
  if (rgb)
    return (
      '#' +
      [rgb[1], rgb[2], rgb[3]]
        .map((v) => Math.min(255, Number(v)).toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase()
    );
  return null;
}

function isTier(t: unknown): t is CelebrationTier {
  return t === 'win' || t === 'big' || t === 'mega' || t === 'jackpot';
}

/**
 * Plays the celebration of `game` at `tier` from `origin`. Anything but win / big / mega / jackpot ('quiet',
 * 'none', null) does nothing. Resolves when the tier's time is over or at once when skipped.
 */
export function celebrate(
  game: string,
  tier: CelebrationTier | string | null | undefined,
  origin?: Origin,
  o: CelebrateOptions = {},
): Promise<void> {
  if (!isTier(tier) || typeof document === 'undefined' || o.signal?.aborted) return Promise.resolve();
  const preset = tier === 'jackpot' ? jackpot : (PRESETS[game] ?? fallback);
  const at = originPoint(origin);
  const own = (o.palette ?? []).map(hex).filter((c): c is string => c !== null);
  const pal = own.length ? (own.length === 1 ? shades(own[0] ?? '#FFD166') : own) : preset.pal;
  const ms = CELEBRATION_MS[tier];
  o.signal?.addEventListener('abort', cutCelebrations, { once: true });
  if (fxConfig.reduced) {
    softGlow(at, tier, pal[0] ?? '#FFD166', ms);
    if ((tier === 'mega' || tier === 'jackpot') && o.amount) amountLabel(o.amount, ms, true);
    return hold(Math.round(ms * 0.6));
  }
  const s: Stage = {
    x: at.x,
    y: at.y,
    W: innerWidth,
    H: innerHeight,
    pal,
    light: preset.light,
    ink: cssVar('--text', '#E4EAF4'),
  };
  try {
    if (tier === 'win') preset.win(s);
    else if (tier === 'big') preset.big(s);
    else preset.mega(s);
  } catch {
    /* a broken frame must never break the game */
  }
  if ((tier === 'mega' || tier === 'jackpot') && o.amount) amountLabel(o.amount, ms, false);
  return hold(ms);
}

/** Reduced motion: a soft glow at the origin (win) or over the screen (big and up) — opacity only, no movement. */
function softGlow(at: { x: number; y: number }, tier: CelebrationTier, col: string, ms: number): void {
  const el = document.createElement('div');
  el.dataset.fxCelGlow = tier;
  const r = tier === 'win' ? 140 : Math.max(innerWidth, innerHeight) * 0.6;
  el.style.cssText = `position:absolute;inset:0;opacity:0;background:radial-gradient(circle ${Math.round(r)}px at ${Math.round(at.x)}px ${Math.round(at.y)}px, ${col}55, ${col}00);`;
  getLayer().appendChild(el);
  const fade = el.animate?.(
    [{ opacity: 0 }, { opacity: 1, offset: 0.2 }, { opacity: 1, offset: 0.7 }, { opacity: 0 }],
    { duration: ms, fill: 'forwards' },
  );
  const remove = () => {
    fade?.cancel?.();
    el.remove();
  };
  const off = onCut(remove);
  after(ms, () => {
    off();
    remove();
  });
}

/** The big amount over the screen (mega / jackpot when the game screen has no overlay of its own). */
function amountLabel(text: string, ms: number, reduced: boolean): void {
  const el = document.createElement('div');
  el.dataset.fxCelAmount = '';
  el.textContent = text;
  el.style.cssText =
    'position:absolute;left:50%;top:44%;transform:translate(-50%,-50%);white-space:nowrap;font-weight:900;' +
    'font-size:clamp(44px,9vw,120px);line-height:1;letter-spacing:-.02em;font-variant-numeric:tabular-nums;' +
    'background:linear-gradient(180deg,#FFF1C2 0%,#FFD166 45%,#F2A81D 100%);-webkit-background-clip:text;background-clip:text;color:transparent;' +
    'filter:drop-shadow(0 4px 18px rgba(255,180,40,.55)) drop-shadow(0 2px 0 rgba(90,50,0,.6));opacity:0;';
  getLayer().appendChild(el);
  const frames: Keyframe[] = reduced
    ? [{ opacity: 0 }, { opacity: 1, offset: 0.12 }, { opacity: 1, offset: 0.85 }, { opacity: 0 }]
    : [
        { opacity: 0, transform: 'translate(-50%,-50%) scale(.55)' },
        { opacity: 1, transform: 'translate(-50%,-50%) scale(1.08)', offset: 0.1 },
        { opacity: 1, transform: 'translate(-50%,-50%) scale(1)', offset: 0.18 },
        { opacity: 1, transform: 'translate(-50%,-50%) scale(1.02)', offset: 0.85 },
        { opacity: 0, transform: 'translate(-50%,-50%) scale(1.05)' },
      ];
  const an = el.animate?.(frames, { duration: ms, easing: 'ease-out', fill: 'forwards' });
  const remove = () => {
    an?.cancel?.();
    el.remove();
  };
  const off = onCut(remove);
  after(ms, () => {
    off();
    remove();
  });
}

/** Palettes of the Roulette sectors (pass as `palette`). */
export { ROULETTE_COLORS };
export {
  cutCelebrations,
  skipCelebrations,
  busy as celebrating,
  setParticleCap,
  seedCelebrations,
} from './engine';
/** Live particles (tests, debug). */
export const celebrationParticles = liveParticles;
export { particleCap as celebrationCap };
